import angular from 'angular';

export default angular
  .module('csm.util.LocalDateSerializer', [])

  /**
* @ngdoc    service
* @module   csm.util
* @name     csm.util.LocalDateSerializer.type:LocalDateSerializer
* @requires $http
* 
* @description
* Utility for serializing client (local) dates, before submitting them to the
* server, to bypass the automatic adjustment by timezone. 
* 
* By default, local dates are serialized with a call to `Date.toJSON()` which 
* converts the `Date` object to UTC (Zulu) according to the ISO 8601 standard. 
* For example, `Date.toJSON()` will serialize `12/18/2017 @ 12:53:18 EST` to 
* `2017-12-18T17:53:18.833Z`. The server does not know the client's timezone so 
* it will interpret the time in UTC and convert it to the server's timezone
* (PST) as `12/18/2017 @ 9:53:18 PST`.
* 
* This utility serializes local dates to a long number date stamp value in the 
* format `YYYYMMDDssssssss` where "ssssssss" is the number of milliseconds since 
* midnight. The companion ``**LocalDateSerializer**`` on the server deserializes 
* this value to the client date but in the server's timezone. In the above
* example, the date would be deserialized as `12/18/2017 @ 12:53:18 PST`.
*
* **** Notes ****
* 
*   * This factory is created as a constant so it can be available during the
*       configuration phase.
*       
*   * This module has a `.config` function that loads an intercepter to $http
*     calls that will convert all dates to/from their serialized values.
*
* @example
  <example module="dateSerializerExample">
    <file name="index.html">
      <div ng-controller="dateSerializerController">
        <h1>Test Serialize</h1>
        <label >Enter Date:</label>
        <input type="text" style="width:400px" ng-model-options="{getterSetter:true, updateOn: 'blur'}" ng-model="dateIn"/>
        <button type="button" ng-click="serialize()">Serialize</button>
        <br />   
        <hr />
        <h1>Test Deserialize</h1>
        <label >Enter stamp:</label>
        <input type="text" style="width:400px" ng-model="dateStamp"/>
        <button type="button" ng-click="deserialize()">Deserialize</button>
      </div>
    </file>
    <file name="script.js">
      angular.module('dateSerializerExample', ['csm.util.LocalDateSerializer'])
      .controller("dateSerializerController", function ($scope, $filter, LocalDateSerializer)
        {
        var mDate = new Date();
        $scope.dateIn = function(val)
          {
          if (arguments.length == 0)
            return mDate;
          
          mDate = angular.isDate(val) ? val : new Date(val);
          };
          
        $scope.serialize = function()
          {
          $scope.dateStamp = LocalDateSerializer.serialize(mDate);      
          };
          
        $scope.deserialize = function()
          {
          mDate = LocalDateSerializer.deserialize($scope.dateStamp);
          };
        });
    </file>
  </example>
*******************************************************************************/
  .constant('LocalDateSerializer', {
    deserialize: deserialize,
    deserializeDatesOnObject: deserializeDatesOnObject,
    serialize: serialize,
    serializeDatesOnObject: serializeDatesOnObject
  })
  .config(addInterceptor).name;

addInterceptor.$inject = ['$httpProvider', 'LocalDateSerializer'];

/*****************************************************************************
 * Constants
 *****************************************************************************/
/** Stamp property reg ex. */
var REG_EX_STAMP = /^(.+)Stamp$/;

/** Date property reg ex. */
var REG_EX_DATE = /^.*date$/i;

/*****************************************************************************
 * addInterceptor
 *
 * Don't know how to add this to docs, yet. Leaving this here for now.
 *
 * @ngdoc    function
 * @name     addInterceptor
 * @memberof csm.util.LocalDateSerializer
 * @param    {Service} $httpProvider        Global $http provider.
 * @param    {Service} LocalDateSerializer  Date serialization utility.
 *
 * @description
 * Adds an interceptor to $http that will automatically serialize dates to
 * datestamps for all requests and deserialize datestamps to dates for all
 * responses.
 *****************************************************************************/
/* @ngInject */ function addInterceptor($httpProvider, LocalDateSerializer) {
  /*------------------------------*/
  /* Process the request/response */
  /*------------------------------*/
  $httpProvider.interceptors.push(function () {
    return {
      request: function (config) {
        LocalDateSerializer.serializeDatesOnObject(config.data);
        return config;
      },
      response: function (response) {
        LocalDateSerializer.deserializeDatesOnObject(response.data);
        return response;
      }
    };
  });
}

/*****************************************************************************
 * deserialize */
/**
 * @ngdoc    function
 * @name     deserialize
 * @methodOf csm.util.LocalDateSerializer.type:LocalDateSerializer
 * @param    {number}  stamp  Serialized date.
 * @return   {object}         Deserialized `Date` object.
 *
 * @description
 * Deserializes `stamp` into a date object in the local timezone. Expects
 * `stamp` to be serialized as `YYYYMMDDssssssss` where "ssssssss" is the
 * number of milliseconds since midnight.
 *****************************************************************************/
function deserialize(stamp) {
  if (isNaN(stamp) || stamp <= 0) return null;

  /*----------------------------------------*/
  /* Remove the seconds from the date stamp */
  /* so Chrome displays the correct date.   */
  /*----------------------------------------*/
  var YYYYMMDD = Math.floor(stamp / 100000000);
  var ssssssss = stamp % 100000000;
  var date = new Date(
    new Date(
      YYYYMMDD / 10000,
      ((YYYYMMDD / 100) % 100) - 1,
      YYYYMMDD % 100
    ).getTime() + ssssssss
  );
  return date;
}

/*****************************************************************************
 * deserializeDatesOnObject */
/**
 * @ngdoc    function
 * @name     deserializeDatesOnObject
 * @methodOf csm.util.LocalDateSerializer.type:LocalDateSerializer
 * @param    {object|array}  data  Object with date stamps to deserialize.
 * @param    {object|array}  `data` with deserialized dates.
 *
 * @description
 * Companion to `serializeDatesOnObject`. Iterates over object properties and
 * child elements of `data` for properties whose name end with `Stamp`. These
 * properties are deserialized and a new property is added to the object. For
 * example, `startDateStamp` is deserialized to `startDate`.
 *****************************************************************************/
function deserializeDatesOnObject(data) {
  /*--------------------------------------*/
  /* Iterate child elements of data array */
  /*--------------------------------------*/
  if (angular.isArray(data))
    data.forEach(function (o) {
      return deserializeDatesOnObject(o);
    });
  /*-----------------------*/
  /* Check data properties */
  /*-----------------------*/ else if (angular.isObject(data)) {
    for (var prop in data) {
      var value = data[prop];

      /*----------------------------*/
      /* Iterate complex properties */
      /*----------------------------*/
      if (
        angular.isArray(value) ||
        (angular.isObject(value) && !angular.isDate(value))
      )
        deserializeDatesOnObject(value);
      /*-----------------------------------------------------------------*/
      /* Create date value if property matched and remove stamp property */
      /*-----------------------------------------------------------------*/ else if (
        REG_EX_STAMP.test(prop)
      ) {
        data[REG_EX_STAMP.exec(prop)[1]] = deserialize(value);
        delete data[prop];
      }
    }
  }

  return data;
}

/*****************************************************************************
 * serialize */
/**
 * @ngdoc    function
 * @name     serialize
 * @methodOf csm.util.LocalDateSerializer.type:LocalDateSerializer
 * @param    {object}  date  `Date` object.
 * @return   {number}        Serialized `date`.
 * @description
 * Serializes `date` to `YYYYMMDDssssssss` where "ssssssss" is the number of
 * milliseconds since midnight.
 *****************************************************************************/
function serialize(date) {
  if (!date || !angular.isDate(date)) return 0;

  var YYYYMMDD =
    date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();
  var ssssssss =
    date.getTime() -
    new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  var stamp = YYYYMMDD * 100000000 + ssssssss;
  return stamp;
}

/*****************************************************************************
 * serializeDatesOnObject */
/**
 * @ngdoc    function
 * @name     serializeDatesOnObject
 * @methodOf csm.util.LocalDateSerializer.type:LocalDateSerializer
 * @param    {object|array}  data  Object with dates to serialize.
 * @param    {object|array}  `data` with serialized dates.
 *
 * @description
 * Companion to `deserializeDatesOnObject`. Iterates over object properties and
 * child elements of `data` for properties whose name end with `date`. These
 * properties are serialized and a new property is added to the object ending
 * in `Stamp`. For example, `startDate` is serialized to `startDateStamp`. Any
 * previous stamp values are removed before processing properties.
 *****************************************************************************/
function serializeDatesOnObject(data) {
  /*-----------------------------*/
  /* Iterate array data elements */
  /*-----------------------------*/
  if (angular.isArray(data))
    data.forEach(function (o) {
      return serializeDatesOnObject(o);
    });
  /*--------------------*/
  /* Iterate properties */
  /*--------------------*/ else if (angular.isObject(data)) {
    /*---------------------------------*/
    /* Remove any current stamp values */
    /*---------------------------------*/
    for (var prop in data) {
      if (REG_EX_STAMP.test(prop)) delete data[prop];
    }

    /*-------------------------------------------------------*/
    /* Check for 'date' like properties and serialize values */
    /*-------------------------------------------------------*/
    for (var prop in data) {
      var value = data[prop];

      /*---------------------------------*/
      /* Iterate over complex properties */
      /*---------------------------------*/
      if (
        angular.isArray(value) ||
        (angular.isObject(value) && !angular.isDate(value))
      )
        serializeDatesOnObject(value);
      else if (REG_EX_DATE.test(prop)) data[prop + 'Stamp'] = serialize(value);
    }
  }

  return data;
}
